import { useEffect, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation, Autoplay } from 'swiper/modules';

const CHANNEL_ID = 'UC2vCl9eQ4hCjcvzzrBv-9iQ';
const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

const FALLBACK_VIDEOS = [
  "lQs0cMVfNtw", "hI4HyH5zlvo", "5UN02K4UCr0", "0kFf4UllhTM", "1C5bAx8a_t4",
  "DyAnNi7UGeo", "BG52286pH2I", "FaWi0kx2jJo", "7fMXApNEYsY", "Zzo-eiEuY3A",
];

const Sermons = () => {
  const swiperRef = useRef(null);
  const playersRef = useRef({});
  const [ytReady, setYtReady] = useState(false);
  const [videoIds, setVideoIds] = useState(FALLBACK_VIDEOS);

  // Busca os 10 vídeos mais recentes do canal
  useEffect(() => {
    if (!API_KEY || API_KEY === 'SUA_CHAVE_AQUI') return;

    fetch(
      `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&maxResults=10&order=date&type=video&key=${API_KEY}`
    )
      .then((r) => r.json())
      .then((data) => {
        const ids = data.items?.map((item) => item.id.videoId).filter(Boolean);
        if (ids?.length) setVideoIds(ids);
      })
      .catch(() => {});
  }, []);

  // Carrega a YouTube IFrame API
  useEffect(() => {
    if (window.YT) { setYtReady(true); return; }
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    document.getElementsByTagName('script')[0].before(tag);
    window.onYouTubeIframeAPIReady = () => setYtReady(true);
    return () => { window.onYouTubeIframeAPIReady = null; };
  }, []);

  const handleStateChange = useCallback((event) => {
    if (!swiperRef.current?.autoplay) return;
    const anyPlaying = Object.values(playersRef.current).some(
      (p) => p?.getPlayerState?.() === window.YT.PlayerState.PLAYING
    );
    if (event.data === window.YT.PlayerState.PLAYING) {
      swiperRef.current.autoplay.stop();
    } else if (
      (event.data === window.YT.PlayerState.PAUSED ||
        event.data === window.YT.PlayerState.ENDED) &&
      !anyPlaying
    ) {
      swiperRef.current.autoplay.start();
    }
  }, []);

  useEffect(() => {
    if (!ytReady || !swiperRef.current) return;
    videoIds.forEach((_, i) => {
      try {
        if (!playersRef.current[i] && document.getElementById(`player-${i}`)) {
          playersRef.current[i] = new window.YT.Player(`player-${i}`, {
            events: { onStateChange: handleStateChange },
          });
        }
      } catch (e) {}
    });
    const saved = playersRef.current;
    return () => { Object.values(saved).forEach((p) => { try { p?.destroy?.(); } catch (e) {} }); };
  }, [ytReady, videoIds, handleStateChange]);

  return (
    <section id="sermons" className="py-24 bg-[#0a0f1e]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-16">
          <div className="section-label mx-auto w-fit">
            <FontAwesomeIcon icon={faYoutube} /> YouTube
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            Mensagens <span className="text-gradient">Inspiradoras</span>
          </h2>
          <p className="text-gray-500 mt-3">Assista quando e onde quiser</p>
        </div>

        <Swiper
          modules={[Navigation, Autoplay]}
          spaceBetween={16}
          slidesPerView={1}
          navigation
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          loop
          breakpoints={{
            640: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          className="mb-12"
          onSwiper={(swiper) => (swiperRef.current = swiper)}
        >
          {videoIds.map((id, index) => (
            <SwiperSlide key={id}>
              <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden shadow-xl bg-black ring-1 ring-white/5">
                <iframe
                  id={`player-${index}`}
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${id}?enablejsapi=1&origin=${window.location.origin}`}
                  title={`Mensagem ${index + 1}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  playsInline
                  loading="lazy"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="text-center">
          <a
            href="https://www.youtube.com/@lagoinhacidadeverde/streams"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 glass-card px-6 py-3 rounded-xl text-gray-300 hover:text-white border border-white/10 hover:border-white/20 transition-all duration-200 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faYoutube} className="text-red-500" />
            Ver todas as mensagens
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-xs opacity-60" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Sermons;
